import type { SxProps, Theme } from '@mui/material/styles';
import { Box, Button, Typography } from '@mui/material';
import React from 'react';

import { usePreloadImage } from '../hooks/use-preload-image';
import { useUpBreakpoints } from '../providers/BreakpointsProvider';
import LinkList, { ListItemOrientation, ListItemType } from './LinkList';

const coverImageSx =
    (imageUrl?: string): SxProps<Theme> =>
    (theme) => ({
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        height: 200,
        p: 2,
        mb: 1,
        bgcolor: 'background.paper',
        borderRadius: theme.shape.borderRadius,
        transition: 'height 300ms ease-out',
        overflow: 'hidden',
        '&::before': {
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            content: '""',
            opacity: imageUrl ? 1 : 0,
            transition: 'opacity 0.3s ease-out',
            backgroundImage: imageUrl ? `url('${imageUrl}')` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
        },
        '&::after': {
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            content: '""',
            backgroundImage:
                'linear-gradient(180deg, var(--mui-palette-action-hover) 0%, var(--mui-palette-background-default) 100%)',
        },
        [theme.breakpoints.up('sm')]: {
            height: 280,
        },
        [theme.breakpoints.up('md')]: {
            height: 320,
            p: 4,
        },
        [theme.breakpoints.up('xl')]: {
            height: 360,
        },
    });

const SubscribeButton: React.FC<{
    id: string;
    name: string;
    isSubscribed: boolean;
    modifySubscription: () => void;
}> = ({ isSubscribed, modifySubscription }) => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: { xs: 4, sm: 0 },
            ml: { sm: 2 },
            minWidth: { sm: 144 },
        }}
    >
        <Button
            size="medium"
            id={'button-subscribe'}
            variant={isSubscribed ? 'outlined' : 'contained'}
            color={isSubscribed ? 'secondary' : 'primary'}
            onClick={() => modifySubscription()}
        >
            {isSubscribed ? 'Unfollow' : 'Follow'}
        </Button>
    </Box>
);

export interface CategoryCoverProps {
    id: string;
    name: string;
    description: string;
    imageUrl?: string;
    isSubscribed: boolean;
    modifySubscription: () => void;
    categories: {
        id: string;
        name: string;
        url: string;
    }[];
}

const CategoryCover: React.FC<CategoryCoverProps> = ({
    id,
    name,
    description,
    imageUrl,
    categories,
    isSubscribed,
    modifySubscription,
}) => {
    const preloadedImage = usePreloadImage(imageUrl);
    const { sm } = useUpBreakpoints();

    const descriptionAndSubscribe = (
        <Box
            sx={{
                display: 'flex',
                zIndex: 1,
                flexDirection: { xs: 'column-reverse', sm: 'row' },
                alignItems: 'center',
                mt: { xs: 4, sm: 0 },
                justifyContent: { sm: 'space-between' },
            }}
        >
            <Box
                id="category-info"
                sx={{
                    maxWidth: '100%',
                    display: 'flex',
                    flex: '1 1 auto',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'center', sm: 'flex-start' },
                    justifyContent: { sm: 'space-between' },
                    '& > p': { mb: { xs: 0, sm: undefined } },
                }}
            >
                <Typography
                    variant="body1"
                    sx={{
                        color: 'text.secondary',
                        maxWidth: { xs: '100%', sm: 700, xl: 720 },
                        textAlign: { xs: 'center', sm: 'left' },
                        mb: { xs: 4, sm: 0 },
                    }}
                >
                    {description}
                </Typography>
            </Box>
            <SubscribeButton
                id={id}
                name={name}
                isSubscribed={isSubscribed}
                modifySubscription={modifySubscription}
            />
        </Box>
    );

    return (
        <Box sx={{ mb: { xs: 4, md: 2 } }}>
            <Box id="category-cover-image" sx={coverImageSx(preloadedImage)}>
                <Typography
                    variant="h3"
                    sx={{
                        zIndex: 1,
                        textAlign: { xs: 'center', sm: 'left' },
                        mb: { sm: 1 },
                    }}
                >
                    {name}
                </Typography>
                {sm ? descriptionAndSubscribe : undefined}
            </Box>
            {!sm ? descriptionAndSubscribe : undefined}
            {!!categories.length && (
                <Box
                    sx={{
                        mt: 1.9,
                        display: { xs: 'flex', md: 'none' },
                        justifyContent: { xs: 'center', sm: 'initial' },
                    }}
                >
                    <LinkList
                        items={categories}
                        orientation={ListItemOrientation.Horizontal}
                        displayType={ListItemType.Chip}
                        chipVariant={'info'}
                    />
                </Box>
            )}
        </Box>
    );
};
export default CategoryCover;
